import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/OtaArtifacts.css";
import { getAllHubDetails } from "../commonApi";
import DeviceActionCell from "./DeviceActionCell";
import CopyableCellText from "./CopyableCellText";
import moment from "moment";
import { Loader } from "../Loader";

// Operator-facing, read-only view of hubs: same table layout as the admin Devices
// page, sourced from the /hubs endpoint, with the row actions limited to remote
// terminal access only (no add/delete/telemetry/send-command).
const HubDevices = () => {
  const [filteredDeviceData, setFilteredDeviceData] = useState([]);
  const [countPerPage, setCountPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState("desc");
  const expectedPageRef = useRef(0);

  const handleSearchInputChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
    expectedPageRef.current = 0;
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchHubs = async () => {
      setLoading(true);
      try {
        const response = await getAllHubDetails({
          pageNo: page,
          pageSize: countPerPage,
          searchValue: search,
          sortOrder: sortOrder,
        });
        if (isCancelled) return;
        const devices = (response?.data?.content || []).filter(
          (device) => device && typeof device === "object" && device.thingName
        );
        setFilteredDeviceData(devices);
        setTotalRows(response?.data?.totalElements || 0);
      } catch (error) {
        if (!isCancelled) {
          toast.error("Failed to load hubs");
          console.error("Error loading hubs:", error);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchHubs();
    return () => {
      isCancelled = true;
    };
  }, [page, countPerPage, search, sortOrder]);

  const handleSort = (column, sortDirection) => {
    setSortOrder(sortDirection);
    setPage(0);
    expectedPageRef.current = 0;
  };

  return (
    <div className="custom_tab_wrapper">
      <div className="tab-pane fade show active" id="total-client-count" role="tabpanel">
        <ul className="nav nav-tabs" id="myTab">
          <li className="nav-item">
            <button
              className="nav-link active"
              data-bs-toggle="tab"
              data-bs-target="#home"
              type="button"
              role="tab"
            >
              Terminal Access: {totalRows}
            </button>
          </li>
        </ul>

        <div className="tab-pane fade show active" id="home" role="tabpanel">
          <div className="tab_content_wrapper full_height_tab">
            <div className="custom_table_action">
              <div>
                <input
                  className="form-control"
                  type="text"
                  onChange={handleSearchInputChange}
                  placeholder="Search by Name"
                />
              </div>
            </div>
            <div className="table-responsive h-100vh-150px">
              {loading ? (
                <div className="spin-loader">
                  <Loader />
                </div>
              ) : (
                <div className="custom_table customs-table">
                  <DataTable
                    columns={[
                      {
                        name: "User Given Name",
                        selector: (row) => <CopyableCellText value={row?.userAssignedDeviceName} />,
                        sortable: true,
                        sortField: "userAssignedDeviceName",
                        width: "285px",
                      },
                      {
                        name: "Device Name",
                        selector: (row) => <CopyableCellText value={row?.thingName} />,
                        sortable: true,
                        sortField: "thingName",
                        width: "285px",
                      },
                      {
                        name: "Firmware Version",
                        selector: (row) => row?.firmwareVersion || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        width: "180px",
                      },
                      {
                        name: "Hardware Version",
                        selector: (row) => row?.hardwareVersion || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        width: "180px",
                      },
                      {
                        name: "Device Group Name",
                        selector: (row) => row?.thingGroupName || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        sortable: true,
                        sortField: "thingGroupName",
                        width: "285px",
                      },
                      {
                        name: "Device Model",
                        selector: (row) => row?.modelName || <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>,
                        sortable: true,
                        sortField: "modelName",
                        width: "285px",
                      },
                      {
                        name: 'Created Date',
                        selector: row => row.createdDate ? moment(row.createdDate).format('YYYY-MM-DD HH:mm:ss') : '-',
                        sortable: true,
                        sortField: "createdDate",
                        width: "300px",
                        cell: (row) => (
                          <div style={{ width: "100%" }}>
                            {row?.createdDate ? (
                              moment(row.createdDate).format("YYYY-MM-DD HH:mm:ss")
                            ) : (
                              <span style={{position: 'absolute', left: '58px', top: '18px'}}>—</span>
                            )}
                          </div>
                        ),
                      },
                      {
                        name: "Actions",
                        cell: (row) => (
                          <DeviceActionCell row={row} onlyRemoteTerminal />
                        ),
                      },
                    ]}
                    data={filteredDeviceData}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={countPerPage}
                    paginationDefaultPage={page + 1}
                    onChangePage={(newPage) => {
                      setPage(newPage - 1);
                      expectedPageRef.current = newPage - 1;
                    }}
                    onChangeRowsPerPage={(newPerPage) => {
                      setCountPerPage(newPerPage);
                      setPage(0);
                      expectedPageRef.current = 0;
                    }}
                    highlightOnHover
                    onSort={handleSort}
                    sortServer
                    fixedHeader
                    fixedHeaderScrollHeight="440px"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubDevices;
